import React from 'react';

import { Typography } from '@mui/material';
import { TableCellDate } from '@europrocurement/flexy-components';
import { ColumnDatatable } from '@europrocurement/flexy-datatable';
import { FactureOpenSearch } from '@europrocurement/l2d-domain';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import useFactureColumnsRenderFunctions from './useFactureColumnsRenderFunctions';

const useFactureColumns = () => {
    const { referenceDossierColumnRender, statusColumnRender, societeOpereeColumnRender } =
        useFactureColumnsRenderFunctions();

    const numeroFactureColumn: ColumnDatatable = {
        label: 'N° Facture',
        render: ({ factureNumero }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                sx={{ color: 'text.gris70' }}
            >
                {factureNumero}
            </Typography>
        ),
        field: 'factureNumero',
        isDisplayed: true,
        sortable: true,
    };

    const referencesDossierColumn: ColumnDatatable = {
        label: 'Références dossiers',
        render: ({ dossiers, reference }: FactureOpenSearch) =>
            referenceDossierColumnRender(dossiers, reference),
        isDisplayed: true,
        sortable: true,
        field: 'reference',
    };

    const societeOpereeColumn: ColumnDatatable = {
        label: 'Société opérée',
        render: ({ dossiers }: FactureOpenSearch) => societeOpereeColumnRender(dossiers),
        isDisplayed: true,
        sortable: false,
    };

    const societeFactureeColumn: ColumnDatatable = {
        label: 'Société facturée',
        render: ({ factureClientRaisonSociale }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {' '}
                {factureClientRaisonSociale as string}
            </Typography>
        ),
        isDisplayed: true,
        sortable: true,
        field: 'societeFacturee',
    };

    const typeFormaliteColumn: ColumnDatatable = {
        label: 'Type de formalité',
        render: ({ typeFormalite }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {typeFormalite}
            </Typography>
        ),
        isDisplayed: true,
        field: 'typeFormalite',
        sortable: true,
    };

    const provisionColumn: ColumnDatatable = {
        label: 'Provision',
        render: ({ montantPaiementRecu }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {montantPaiementRecu && `${montantPaiementRecu} €`}
            </Typography>
        ),
        isDisplayed: true,
        sortable: true,
        field: 'montantPaiementRecu',
    };
    const montantTTCColumn: ColumnDatatable = {
        label: 'Montant TTC',
        render: ({ totalTtc }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {totalTtc && `${totalTtc} €`}
            </Typography>
        ),
        isDisplayed: true,
        sortable: true,
    };

    const dateColumn: ColumnDatatable = {
        label: 'Date',
        render: ({ dateFacture }: FactureOpenSearch) =>
            dateFacture && (
                <TableCellDate
                    date={dateFacture}
                    displayCalendar
                />
            ),
        isDisplayed: true,
        sortable: true,
        field: 'dateFacture',
    };

    const dateLimiteColumn: ColumnDatatable = {
        label: 'Échéance',
        render: ({ dateEcheanceFacture }: FactureOpenSearch) =>
            dateEcheanceFacture && (
                <TableCellDate
                    date={dateEcheanceFacture}
                    displayCalendar
                />
            ),
        isDisplayed: true,
        sortable: true,
        field: 'dateEcheanceFacture',
    };

    const statutColumn: ColumnDatatable = {
        label: 'Statut',
        render: ({ status }: FactureOpenSearch) => statusColumnRender(status),
        isDisplayed: true,
        sortable: true,
    };

    const prescripteurColumn: ColumnDatatable = {
        label: 'Prescripteur',
        render: ({ prescripteurLibelle }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {prescripteurLibelle}
            </Typography>
        ),
        isDisplayed: true,
        sortable: false,
    };

    const countDossiersColumn: ColumnDatatable = {
        label: 'Dossiers',
        render: ({ dossiers }: FactureOpenSearch) => (
            <>
                <FaOptionIcon
                    icon={['fasl', 'folder']}
                    sx={{ fontSize: '1rem' }}
                />
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {dossiers && `${dossiers.length} `}
                </Typography>
            </>
        ),
        isDisplayed: true,
        sortable: false,
    };

    const columnsFront: ColumnDatatable[] = [
        numeroFactureColumn,
        referencesDossierColumn,
        societeOpereeColumn,
        societeFactureeColumn,
        typeFormaliteColumn,
        provisionColumn,
        montantTTCColumn,
        dateColumn,
        dateLimiteColumn,
        statutColumn,
    ];

    const formalitiesInvoicesColumnsBack: ColumnDatatable[] = [
        numeroFactureColumn,
        referencesDossierColumn,
        prescripteurColumn,
        societeOpereeColumn,
        societeFactureeColumn,
        typeFormaliteColumn,
        provisionColumn,
        montantTTCColumn,
        dateColumn,
        dateLimiteColumn,
        countDossiersColumn,
        statutColumn,
    ];

    return { columnsFront, formalitiesInvoicesColumnsBack };
};

export default useFactureColumns;
